/**
 * Image Hotspot Editor (Listen & Color)
 * Two-tab editor: Content (title, description, supplementary) and Question (objects, color palette)
 */

import * as React from 'react';
import { Input, Label, Textarea, cn } from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditQuestionsLayout,
} from '@ai-content-hub/content-core';
import {
    ColorDropZoneEdit,
    PageEdit,
    type ColorDropZoneElementData,
    type PageElementData,
} from '@ai-content-hub/content-elements';
import type { ImageHotspotData, ImageHotspotConfig } from './schema';
import { defaultImageHotspotData } from './schema';
import { useLocale } from '@ai-content-hub/content-core';
import { BookOpen, MessageCircleQuestionIcon, Settings } from 'lucide-react';

type TabValue = 'image' | 'content' | 'config';

interface EditorDisplayOptions {
    showTitle?: boolean;
    showDescription?: boolean;
}

export interface ImageHotspotEditProps {
    value: ImageHotspotData;
    onChange: (value: ImageHotspotData) => void;
    config?: ImageHotspotConfig;
    onConfigChange?: (config: ImageHotspotConfig) => void;
    disabled?: boolean;
    editorOptions?: EditorDisplayOptions;
    /** Slot for Configuration panel (from ContentEditor when hasIntegratedConfigTab) */
    configSlot?: React.ReactNode;
    /** Validation errors keyed by field path */
    errors?: Record<string, string>;
    className?: string;
}

export const ImageHotspotEdit: React.FC<ImageHotspotEditProps> = ({
    value: data,
    onChange,
    config: _config,
    onConfigChange: _onConfigChange,
    disabled = false,
    editorOptions,
    configSlot,
    errors,
    className,
}) => {
    const [activeTab, setActiveTab] = React.useState<TabValue>('image');

    const { t } = useLocale({ config: _config?.i18n });

    const safeData: ImageHotspotData = {
        ...defaultImageHotspotData,
        ...data,
        colorDropZoneData: data?.colorDropZoneData ?? defaultImageHotspotData.colorDropZoneData,
        supplementaryContent:
            data?.supplementaryContent ?? defaultImageHotspotData.supplementaryContent,
    };

    const updateData = (updates: Partial<ImageHotspotData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    const handleColorDropZoneChange = (colorDropZoneData: ColorDropZoneElementData) => {
        updateData({ colorDropZoneData });
    };

    const handleSupplementaryChange = (supplementaryContent: PageElementData) => {
        updateData({ supplementaryContent });
    };

    const imageHotspotTabs: EditTabDescriptor[] = [
        { id: 'image', label: t('contentTab', 'Content'), icon: BookOpen },
        { id: 'content', label: t('questionsTab', 'Questions'), icon: MessageCircleQuestionIcon },
        ...(configSlot ? [{ id: 'config', label: t('configTab', 'Configuration'), icon: Settings }] : []),
    ];

    const imageMeta = (
        <div className="image-hotspot-edit-image-form space-y-4">
            {editorOptions?.showTitle !== false && (
                <div className="space-y-2">
                    <Label htmlFor="image-hotspot-title">
                        {t('titleLabel', 'Title')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                        id="image-hotspot-title"
                        value={safeData.title || ''}
                        onChange={(e) => updateData({ title: e.target.value })}
                        placeholder={t('titlePlaceholder', 'Enter title')}
                        disabled={disabled}
                        className="image-hotspot-edit-title-input text-lg font-semibold"
                    />
                </div>
            )}
            {editorOptions?.showDescription !== false && (
                <div className="space-y-2">
                    <Label htmlFor="image-hotspot-description">{t('descLabel', 'Instructions (Optional)')}</Label>
                    <Textarea
                        id="image-hotspot-description"
                        value={safeData.description || ''}
                        onChange={(e) => updateData({ description: e.target.value })}
                        placeholder={t('descPlaceholder', 'Enter instructions...')}
                        disabled={disabled}
                        rows={3}
                        className="image-hotspot-edit-description-input"
                    />
                </div>
            )}
        </div>
    );

    const hideHeader =
        editorOptions?.showTitle === false && editorOptions?.showDescription === false;

    return (
        <CompositeEditShell
            prefix="image-hotspot-edit"
            tabs={imageHotspotTabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as TabValue)}
            configSlot={configSlot}
            className={cn('image-hotspot-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'image') {
                    if (hideHeader) {
                        return (
                            <div className="image-hotspot-edit-image-panel flex-1 min-h-0 overflow-y-auto px-1">
                                <PageEdit
                                    value={
                                        safeData.supplementaryContent ??
                                        defaultImageHotspotData.supplementaryContent!
                                    }
                                    onChange={handleSupplementaryChange}
                                    disabled={disabled}
                                    hideSettings
                                    className="image-hotspot-edit-supplementary-inner"
                                />
                            </div>
                        );
                    }
                    return (
                        <CompositeEditContentTab
                            prefix="image-hotspot-edit"
                            meta={imageMeta}
                            page={
                                <PageEdit
                                    value={
                                        safeData.supplementaryContent ??
                                        defaultImageHotspotData.supplementaryContent!
                                    }
                                    onChange={handleSupplementaryChange}
                                    disabled={disabled}
                                    hideSettings
                                    className="image-hotspot-edit-supplementary-inner"
                                />
                            }
                        />
                    );
                }
                if (tabId === 'content') {
                    const zonesError = errors?.['colorDropZoneData.zones'] ??
                        ((safeData.colorDropZoneData?.zones?.length ?? 0) === 0
                            ? t('zonesErrorMsg', 'You must draw at least 1 hotspot on the image.')
                            : undefined);
                    return (
                        <CompositeEditQuestionsLayout
                            variant="detail-only"
                            prefix="image-hotspot-edit"
                            detail={
                                <div className="image-hotspot-edit-questions-wrapper">
                                    <ColorDropZoneEdit
                                        value={safeData.colorDropZoneData}
                                        onChange={handleColorDropZoneChange}
                                        disabled={disabled}
                                        className="image-hotspot-edit-color-drop-zone-inner"
                                    />
                                    {zonesError && (
                                        <p className="image-hotspot-edit-error text-sm text-destructive mt-2 px-1">
                                            {zonesError}
                                        </p>
                                    )}
                                </div>
                            }
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default ImageHotspotEdit;
